import { GiftCardField } from './GiftCardField.js';
import { useEditPanelViewport } from '../../ui/use-edit-panel-viewport.js';
import { formatCrmMoney, formatCrmPriceLabel } from './crm-money.js';
import { SESSION_STATUS_LABELS, type PricingPlanItem } from '@diana/contracts';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, X } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { useDianaUIPortal } from '../../ui/index.js';
import { SelectField, TextAreaField, TextField } from '../settings/SettingsEditor.js';
import { sessionStatuses } from './crm-format.js';
import { CompactShootSchedule } from './CompactShootSchedule.js';
import type { ShootDraft } from './shoot-conflict.js';
import { packagePrice } from './package-price.js';
import './ShootDetailsPanel.css';

export type ShootPanelMode = 'edit' | 'schedule' | 'reschedule' | 'money' | 'deliver';
export type ShootPanelFocus = 'Shoot title' | 'Date and time' | 'Status' | 'Notes' | 'Price';
const titles: Record<ShootPanelMode, string> = {
  edit: 'Shoot details',
  schedule: 'Schedule shoot',
  reschedule: 'Reschedule shoot',
  money: 'Money',
  deliver: 'Complete shoot',
};

export const shootMoney = formatCrmMoney;

export function ShootDetailsPanel({
  mode,
  focus,
  draft,
  clientName,
  additionalClients,
  defaultDurationMinutes,
  packages = [],
  saveStatus,
  saveError,
  conflicted,
  onChange,
  onClose,
  onSave,
  onValidityChange,
  dirty,
  saving,
  onRestoreFocus,
}: {
  mode: ShootPanelMode | null;
  focus: ShootPanelFocus;
  draft: ShootDraft;
  clientName: string;
  additionalClients: string[];
  defaultDurationMinutes?: number;
  packages?: readonly PricingPlanItem[];
  saveStatus: ReactNode;
  saveError: boolean;
  conflicted: boolean;
  onChange(patch: Partial<ShootDraft>): void;
  onClose(): void;
  onSave(patch?: Partial<ShootDraft>): Promise<boolean>;
  onValidityChange(valid: boolean): void;
  dirty: boolean;
  saving: boolean;
  onRestoreFocus(): void;
}) {
  const lastMode = useRef(mode);
  if (mode) lastMode.current = mode;
  const visibleMode = mode ?? lastMode.current;
  const packageOptions = [...packages].sort((a, b) => a.position - b.position);
  const currentPackageMissing =
    draft.requestedPackageKey && !packages.some((plan) => plan.key === draft.requestedPackageKey);
  const packageLabels = Object.fromEntries(
    packageOptions.map((plan) => [
      plan.key,
      `${plan.payload.title} · ${formatCrmPriceLabel(plan.payload.priceLabel)}`,
    ]),
  );
  if (currentPackageMissing)
    packageLabels[draft.requestedPackageKey!] =
      `${draft.requestedPackageTitle ?? 'Current package'}${draft.requestedPackagePriceLabel ? ` · ${formatCrmPriceLabel(draft.requestedPackagePriceLabel)}` : ''} (current)`;
  const [packageError, setPackageError] = useState('');
  const portal = useDianaUIPortal();
  const [timeValid, setTimeValid] = useState(true);
  useEffect(() => {
    onValidityChange(
      mode === 'edit' || mode === 'schedule' || mode === 'reschedule' ? timeValid : true,
    );
  }, [mode, timeValid, onValidityChange]);
  const content = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const { panelRef, bodyRef } = useEditPanelViewport(mode !== null && !conflicted);
  const captureContent = useCallback(
    (node: HTMLDivElement | null) => {
      content.current = node;
      panelRef(node);
    },
    [panelRef],
  );
  const captureBody = useCallback(
    (node: HTMLDivElement | null) => {
      body.current = node;
      bodyRef(node);
    },
    [bodyRef],
  );
  useLayoutEffect(() => {
    if ((!saveError && timeValid) || !mode || conflicted) return;
    const scroller = body.current;
    const active = document.activeElement;
    if (
      !(active instanceof HTMLElement) ||
      !scroller?.contains(active) ||
      !active.matches('input, textarea, select')
    )
      return;
    // The persistent error enlarges the footer without triggering native focus scrolling.
    // Keep the active field and its focus ring inside this panel's newly smaller scroll body.
    const fieldBounds = active.getBoundingClientRect();
    const bodyBounds = scroller.getBoundingClientRect();
    const clearance = 8;
    if (fieldBounds.bottom > bodyBounds.bottom - clearance) {
      scroller.scrollTop += fieldBounds.bottom - bodyBounds.bottom + clearance;
    } else if (fieldBounds.top < bodyBounds.top + clearance) {
      scroller.scrollTop -= bodyBounds.top + clearance - fieldBounds.top;
    }
  }, [saveError, timeValid, mode, conflicted]);
  const title = titles[visibleMode ?? 'edit'];
  const schedule = visibleMode === 'schedule' || visibleMode === 'reschedule';
  const formValid = !(mode === 'edit' || schedule) || timeValid;
  const price = (
    <label className="field">
      <span>Price ({draft.currency})</span>
      <input
        aria-label={`Price (${draft.currency})`}
        type="number"
        min="0"
        step="0.01"
        inputMode="decimal"
        value={draft.priceAmountMinor === null ? '' : String(draft.priceAmountMinor / 100)}
        onChange={(event) => {
          const value = event.currentTarget.value;
          onChange({
            priceAmountMinor: value === '' ? null : Math.max(0, Math.round(Number(value) * 100)),
          });
        }}
      />
    </label>
  );
  const when = (
    <>
      <CompactShootSchedule
        showTimezone={false}
        defaultDurationMinutes={defaultDurationMinutes}
        scheduledStartAt={draft.scheduledStartAt}
        scheduledEndAt={draft.scheduledEndAt}
        onChange={onChange}
        onValidityChange={setTimeValid}
      />
      <TextField
        label="Location"
        optional
        value={draft.location ?? ''}
        onChange={(location) => onChange({ location: location || null })}
      />
    </>
  );
  return (
    <Dialog.Root
      open={mode !== null && !conflicted}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal container={portal}>
        <Dialog.Overlay className="shoot-panel__overlay" />
        <Dialog.Content
          ref={captureContent}
          inert={mode === null || conflicted}
          className={
            visibleMode === 'edit' ? 'shoot-panel dui-edit-panel' : 'shoot-panel dui-compact-panel'
          }
          data-edit-panel={visibleMode === 'edit' ? 'true' : undefined}
          data-compact-panel={visibleMode !== 'edit' ? '' : undefined}
          data-mode={visibleMode}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            if (window.matchMedia('(max-width: 1023px)').matches) {
              content.current
                ?.querySelector<HTMLElement>('[aria-label="Close shoot details"]')
                ?.focus({ preventScroll: true });
              return;
            }
            const name =
              mode === 'deliver'
                ? 'Mark as delivered'
                : mode === 'money'
                  ? `Price (${draft.currency})`
                  : schedule
                    ? 'Date'
                    : focus;
            content.current?.querySelector<HTMLElement>(`[aria-label="${name}"]`)?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (!conflicted) onRestoreFocus();
          }}
        >
          <header className="shoot-panel__header" data-panel-header>
            <div>
              <p>{clientName}</p>
              <Dialog.Title>{title}</Dialog.Title>
            </div>
            <Dialog.Close
              className="shoot-button shoot-button--outline shoot-button--icon"
              aria-label="Close shoot details"
            >
              <X size={20} aria-hidden="true" />
            </Dialog.Close>
          </header>
          <Dialog.Description className="sr-only">
            Changes are saved only when you press Save or confirm an action. Closing discards
            unsaved changes after confirmation.
          </Dialog.Description>
          <div ref={captureBody} className="shoot-panel__body" data-panel-body>
            {visibleMode === 'edit' ? (
              <>
                <div className="shoot-panel__group shoot-edit-group">
                  <TextField
                    label="Shoot title"
                    value={draft.title}
                    onChange={(title) => onChange({ title })}
                  />
                  {draft.galleryUrl || draft.status === 'Delivered' ? (
                    <TextField
                      label="Gallery link"
                      value={draft.galleryUrl ?? ''}
                      onChange={(value) => onChange({ galleryUrl: value.trim() || null })}
                    />
                  ) : null}
                  <SelectField
                    label="Status"
                    value={draft.status}
                    options={sessionStatuses.filter(
                      (status) => status !== 'Canceled' || draft.status === 'Canceled',
                    )}
                    optionLabels={SESSION_STATUS_LABELS}
                    onChange={(status) => onChange({ status: status as ShootDraft['status'] })}
                  />
                  <GiftCardField
                    checked={draft.isGiftCard ?? false}
                    disabled={saving}
                    onCheckedChange={(isGiftCard) => onChange({ isGiftCard })}
                  />
                  <div className="shoot-edit-grid--package crm-form-pair">
                    <SelectField
                      label="Package"
                      value={draft.requestedPackageKey ?? ''}
                      options={[
                        '',
                        ...(currentPackageMissing ? [draft.requestedPackageKey!] : []),
                        ...packageOptions.map((plan) => plan.key),
                      ]}
                      optionLabels={{ '': 'No package', ...packageLabels }}
                      onChange={(key) => {
                        setPackageError('');
                        const plan = packages.find((item) => item.key === key);
                        if (!key) {
                          onChange({
                            requestedPackageKey: null,
                            requestedPackageTitle: null,
                            requestedPackagePriceLabel: null,
                          });
                          return;
                        }
                        if (!plan || key === draft.requestedPackageKey) return;
                        const price = packagePrice(plan.payload.priceLabel);
                        if (
                          price &&
                          price.currency !== draft.currency &&
                          (draft.expensesAmountMinor > 0 || (draft.transactions?.length ?? 0) > 0)
                        ) {
                          setPackageError(
                            `This package uses ${price.currency}, but this shoot has financial records in ${draft.currency}. The package and price were not changed.`,
                          );
                          return;
                        }

                        onChange({
                          requestedPackageKey: plan.key,
                          requestedPackageTitle: plan.payload.title,
                          requestedPackagePriceLabel: plan.payload.priceLabel,
                          priceAmountMinor: price?.amountMinor ?? null,
                          ...(price ? { currency: price.currency } : {}),
                        });
                      }}
                    />
                    {price}
                  </div>
                  {packageError ? (
                    <p className="shoot-time-fields__error" role="alert">
                      {packageError}
                    </p>
                  ) : null}
                  {additionalClients.length ? (
                    <p className="shoot-edit-context">
                      Also linked: {additionalClients.join(', ')}
                    </p>
                  ) : null}
                </div>
                <div className="shoot-panel__group shoot-edit-group">
                  <div className="shoot-edit-timing">{when}</div>
                </div>
                <div className="shoot-panel__group shoot-edit-group">
                  <div className="shoot-edit-notes">
                    <TextAreaField
                      label="Notes"
                      optional
                      value={draft.internalNotes ?? ''}
                      onChange={(internalNotes) =>
                        onChange({ internalNotes: internalNotes || null })
                      }
                    />
                  </div>
                </div>
              </>
            ) : null}
            {schedule ? (
              <>
                <CompactShootSchedule
                  scheduledStartAt={draft.scheduledStartAt}
                  scheduledEndAt={draft.scheduledEndAt}
                  defaultDurationMinutes={defaultDurationMinutes}
                  onChange={onChange}
                  onValidityChange={setTimeValid}
                />
                <TextField
                  label="Location"
                  optional
                  value={draft.location ?? ''}
                  onChange={(location) => onChange({ location: location || null })}
                />
              </>
            ) : null}
            {visibleMode === 'money' ? (
              <>
                <fieldset className="shoot-panel__group">
                  <legend>Shoot price</legend>
                  {price}
                </fieldset>
                <dl className="shoot-panel__summary">
                  <div>
                    <dt>Expenses</dt>
                    <dd>{shootMoney(draft.expensesAmountMinor, draft.currency)}</dd>
                  </div>
                  <div>
                    <dt>Net received</dt>
                    <dd>
                      {shootMoney(
                        (draft.transactions ?? [])
                          .filter((item) => item.kind === 'Income')
                          .reduce((sum, item) => sum + item.amountMinor, 0) -
                          draft.expensesAmountMinor,
                        draft.currency,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Currency</dt>
                    <dd>{draft.currency}</dd>
                  </div>
                </dl>
                <p className="shoot-panel__hint">
                  Record payments and expenses in the Money block. Currency is locked once
                  transactions exist.
                </p>
              </>
            ) : null}
            {visibleMode === 'deliver' ? (
              <div className="shoot-panel__delivery">
                <span aria-hidden="true">
                  <Check size={28} />
                </span>
                <h3>Ready to mark this shoot as delivered?</h3>
                <p>Only the shoot status changes. No message is sent to the client.</p>
              </div>
            ) : null}
          </div>
          <footer className="shoot-panel__footer" data-panel-footer>
            {saveStatus}
            {!formValid ? (
              <p className="shoot-time-fields__error" role="status">
                Correct the date, time or duration before saving.
              </p>
            ) : null}
            <div className="shoot-panel__actions">
              <button
                className="shoot-button shoot-button--primary"
                aria-busy={saving}
                disabled={
                  saving || !formValid || (!dirty && mode !== 'schedule' && mode !== 'deliver')
                }
                onClick={async () => {
                  const patch =
                    mode === 'schedule'
                      ? {
                          status: 'Scheduled' as const,
                          ...(draft.scheduledStartAt && !draft.scheduledEndAt
                            ? {
                                scheduledEndAt: new Date(
                                  Date.parse(draft.scheduledStartAt) +
                                    (defaultDurationMinutes ?? 60) * 60000,
                                ).toISOString(),
                              }
                            : {}),
                        }
                      : mode === 'deliver'
                        ? { status: 'Delivered' as const }
                        : undefined;
                  if (await onSave(patch)) onClose();
                }}
              >
                {saving
                  ? 'Saving…'
                  : visibleMode === 'schedule'
                    ? 'Schedule'
                    : visibleMode === 'deliver'
                      ? 'Mark as delivered'
                      : 'Save'}
              </button>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
